import { useCallback, useRef } from 'react'
import { useInternalAnalytics } from '../../../utils/internalAnalytics'
import { useMParticleGlobalProperties } from '../../../utils/internalAnalytics/internalAnalyticsUtils'
import { useMParticleEventProperties } from './useMParticleEventProperties'
import { useFeatureFlag } from '../../optimizely/hooks/useFeatureFlag'
import { OptimizelyFeatureFlags } from '../../../models/Optimizely'

/**
 * Hook that returns a function to track page views using mParticle.logPageView
 */
const useTrackPageView = () => {
  const { mParticle, consentSynced } = useInternalAnalytics()
  const mParticleEventProperties = useMParticleEventProperties()
  const mParticleGlobalProperties = useMParticleGlobalProperties()

  // Use useRef to track the last step within this hook instance
  const lastTrackedStepRef = useRef<string | undefined>()

  const trackPageView = useCallback(
    (stepName?: string) => {
      // Check if we already tracked this step
      if (stepName && stepName === lastTrackedStepRef.current) {
        return
      }

      if (!mParticle || !consentSynced || !mParticleGlobalProperties) {
        return
      }

      const attributesWithCommonProperties = {
        ...mParticleGlobalProperties,
        page_title: document.title || stepName || '',
        page_url: window.location.href,

        npo_id: mParticleEventProperties.npo_id,
        npo_name: mParticleEventProperties.npo_name,
        campaign_id: mParticleEventProperties.campaign_id,
      }

      const customFlags = {
        'GA4.Title': document.title,
        'GA4.Location': document.location.href,
        'GA4.Referrer': document.referrer,
      }

      mParticle.logPageView('Page', attributesWithCommonProperties, customFlags)

      // Update the last tracked step
      lastTrackedStepRef.current = stepName
    },
    [mParticle, consentSynced, mParticleEventProperties, mParticleGlobalProperties],
  )

  return trackPageView
}

export default useTrackPageView
